import { useEffect, useState, useMemo, useRef } from 'react'
import { TypeDocRoot, ResolvedFunction } from '../types/functions'
import { normalizeFunctionsData } from '../utils/functionDocUtils'
import { FunctionSidebar } from './functions/FunctionSidebar'
import { FunctionDetail } from './functions/FunctionDetail'
import { FunctionCategoryOverview } from './functions/FunctionCategoryOverview'
import { BookOpen, ListFilter } from 'lucide-react'
import '../styles/function-docs.css'

interface FunctionDocRendererProps {
  specUrl: string
}

export default function FunctionDocRenderer({ specUrl }: FunctionDocRendererProps) {
  const [data, setData] = useState<TypeDocRoot | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeFunctionId, setActiveFunctionId] = useState<string | null>(null)
  const [activeCategory, setActiveCategory] = useState<{ categoryName: string; moduleName: string } | null>(null)
  const [viewMode, setViewMode] = useState<'page' | 'list'>('page')
  const [searchQuery, setSearchQuery] = useState('')
  const isScrollingRef = useRef(false)

  // Fetch TypeDoc JSON
  useEffect(() => {
    async function load() {
      try {
        setLoading(true)
        setError(null)
        const res = await fetch(specUrl)
        if (!res.ok) throw new Error(`Failed to fetch function documentation (${res.status} ${res.statusText})`)
        const json = await res.json()
        const actualData = json.data || json
        setData(actualData)
      } catch (e: any) {
        setError(e.message || 'Failed to load functions specification')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [specUrl])

  // Normalize data with structural taxonomy
  const modules = useMemo(() => {
    if (!data) return []
    return normalizeFunctionsData(data)
  }, [data])

  // Flatten all functions for quick lookup and sequential pagination
  const allFunctions = useMemo(() => {
    const list: ResolvedFunction[] = []
    for (const mod of modules) {
      for (const cat of mod.categories) {
        for (const fn of cat.functions) {
          list.push(fn)
        }
      }
    }
    return list
  }, [modules])

  // Initial anchor / hash routing and hash change listening
  useEffect(() => {
    if (allFunctions.length === 0) return

    const handleHash = () => {
      const hash = window.location.hash.replace(/^#/, '').trim()
      if (!hash) {
        if (!activeFunctionId && allFunctions[0]) {
          setActiveFunctionId(allFunctions[0].id)
        }
        return
      }

      // Check if hash is a category link, e.g. "category-http-network"
      if (hash.startsWith('category-')) {
        const slug = hash.replace(/^category-/, '').toLowerCase()
        for (const mod of modules) {
          for (const cat of mod.categories) {
            const catSlug = cat.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')
            if (catSlug === slug) {
              setActiveCategory({ categoryName: cat.name, moduleName: mod.name })
              setActiveFunctionId(null)
              return
            }
          }
        }
      }

      // Check if hash matches a function
      const cleanHash = hash.replace(/^fn-/, '').toLowerCase()
      const match = allFunctions.find(
        f =>
          f.anchorId.toLowerCase() === cleanHash ||
          f.id.toLowerCase() === cleanHash ||
          f.name.toLowerCase() === cleanHash
      )

      if (match) {
        setActiveFunctionId(match.id)
        setActiveCategory(null)
        if (viewMode === 'list') {
          setTimeout(() => {
            const el = document.getElementById(`fn-${match.anchorId}`) || document.getElementById(match.anchorId)
            if (el) {
              el.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }
          }, 100)
        }
      } else if (!activeFunctionId && allFunctions[0]) {
        setActiveFunctionId(allFunctions[0].id)
      }
    }

    handleHash()
    window.addEventListener('hashchange', handleHash)
    return () => window.removeEventListener('hashchange', handleHash)
  }, [allFunctions, modules, viewMode])

  // Scroll spy in continuous 'list' mode
  useEffect(() => {
    if (viewMode !== 'list' || allFunctions.length === 0) return

    const handleScroll = () => {
      if (isScrollingRef.current) return

      const cards = document.querySelectorAll('.ns-function-card')
      const scrollPosition = window.scrollY + 140

      for (let i = cards.length - 1; i >= 0; i--) {
        const card = cards[i] as HTMLElement
        if (card.offsetTop <= scrollPosition) {
          const id = card.id.replace(/^fn-/, '')
          const match = allFunctions.find(f => f.anchorId === id || f.name === id)
          if (match && match.id !== activeFunctionId) {
            setActiveFunctionId(match.id)
          }
          break
        }
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [viewMode, allFunctions, activeFunctionId])

  // Selection handlers
  const handleSelectFunction = (fn: ResolvedFunction) => {
    setActiveFunctionId(fn.id)
    setActiveCategory(null)
    window.history.replaceState(null, '', `#${fn.anchorId}`)

    if (viewMode === 'page') {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } else {
      const el = document.getElementById(`fn-${fn.anchorId}`) || document.getElementById(fn.anchorId)
      if (el) {
        isScrollingRef.current = true
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        setTimeout(() => {
          isScrollingRef.current = false
        }, 700)
      }
    }
  }

  const handleSelectCategory = (categoryName: string, moduleName: string) => {
    setActiveCategory({ categoryName, moduleName })
    const catSlug = categoryName.toLowerCase().replace(/[^a-z0-9]+/g, '-')
    window.history.replaceState(null, '', `#category-${catSlug}`)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // Active Function and pagination indices
  const currentFunctionIndex = useMemo(() => {
    if (!activeFunctionId) return 0
    return allFunctions.findIndex(f => f.id === activeFunctionId || f.anchorId === activeFunctionId)
  }, [allFunctions, activeFunctionId])

  const currentFunction = useMemo(() => {
    if (currentFunctionIndex >= 0 && allFunctions[currentFunctionIndex]) {
      return allFunctions[currentFunctionIndex]
    }
    return allFunctions[0] || null
  }, [allFunctions, currentFunctionIndex])

  const prevFunction = useMemo(() => {
    if (currentFunctionIndex > 0) {
      return allFunctions[currentFunctionIndex - 1]
    }
    return null
  }, [allFunctions, currentFunctionIndex])

  const nextFunction = useMemo(() => {
    if (currentFunctionIndex >= 0 && currentFunctionIndex < allFunctions.length - 1) {
      return allFunctions[currentFunctionIndex + 1]
    }
    return null
  }, [allFunctions, currentFunctionIndex])

  // Functions for active category overview
  const categoryFunctions = useMemo(() => {
    if (!activeCategory) return []
    for (const mod of modules) {
      if (mod.name === activeCategory.moduleName) {
        const found = mod.categories.find(c => c.name === activeCategory.categoryName)
        if (found) return found.functions
      }
    }
    return []
  }, [modules, activeCategory])

  if (loading) {
    return (
      <div className="card" style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading functions documentation...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="card" role="alert" style={{ padding: '1.5rem', borderColor: '#dc2626' }}>
        <h3 style={{ color: '#dc2626', marginTop: 0 }}>Documentation Load Error</h3>
        <p>{error}</p>
        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
          Target spec URL: <code>{specUrl}</code>
        </p>
      </div>
    )
  }

  return (
    <div className="ns-function-doc-wrapper">
      {/* OpenAPI-Style Navigation Sub-Menu */}
      <FunctionSidebar
        modules={modules}
        activeFunctionId={activeCategory ? null : currentFunction?.id || null}
        activeCategoryId={activeCategory ? `${activeCategory.moduleName}::${activeCategory.categoryName}` : null}
        onSelectFunction={handleSelectFunction}
        onSelectCategory={handleSelectCategory}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
      />

      {/* Main Documentation Area */}
      <main className="ns-function-content-area" style={{ flex: 1, minWidth: 0 }}>
        {/* Top View Mode & Information Toolbar */}
        <div className="ns-view-mode-bar">
          <div className="ns-view-mode-info">
            <span>
              <strong>{allFunctions.length}</strong> Script Functions
            </span>
            <span>·</span>
            <span>{modules.length} Modules</span>
          </div>

          <div className="ns-view-mode-toggle">
            <button
              type="button"
              className={`ns-view-btn ${viewMode === 'page' ? 'is-active' : ''}`}
              onClick={() => setViewMode('page')}
              title="Separate pages per function (Focused OpenAPI View)"
            >
              <BookOpen size={14} />
              <span>Page View</span>
            </button>
            <button
              type="button"
              className={`ns-view-btn ${viewMode === 'list' ? 'is-active' : ''}`}
              onClick={() => setViewMode('list')}
              title="Continuous scroll for all functions"
            >
              <ListFilter size={14} />
              <span>All Functions</span>
            </button>
          </div>
        </div>

        {/* View Mode: Focused Separated Pages */}
        {viewMode === 'page' ? (
          activeCategory ? (
            <FunctionCategoryOverview
              moduleName={activeCategory.moduleName}
              categoryName={activeCategory.categoryName}
              functions={categoryFunctions}
              onSelectFunction={handleSelectFunction}
            />
          ) : currentFunction ? (
            <FunctionDetail
              key={currentFunction.id}
              func={currentFunction}
              prevFunc={prevFunction}
              nextFunc={nextFunction}
              onSelectFunction={handleSelectFunction}
              onSelectCategory={handleSelectCategory}
            />
          ) : (
            <div className="card" style={{ padding: '2rem', textAlign: 'center' }}>
              <p>Select a function from the left menu to view its documentation.</p>
            </div>
          )
        ) : (
          /* View Mode: Continuous Scroll */
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
            {modules.map(mod => (
              <section key={mod.name} className="ns-module-section">
                <div className="ns-module-section-banner">
                  <h2 className="ns-module-banner-title">{mod.name}</h2>
                </div>

                {mod.categories.map(cat => (
                  <div key={cat.name} className="ns-category-section">
                    <div className="ns-category-section-banner">
                      <h3 className="ns-category-banner-title">{cat.name}</h3>
                      <span className="ns-category-count">{cat.functions.length}</span>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                      {cat.functions.map(fn => (
                        <FunctionDetail
                          key={fn.id}
                          func={fn}
                          onSelectCategory={handleSelectCategory}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </section>
            ))}
          </div>
        )}
      </main>
    </div>
  )
}
